import { downscaleImageBlobToJpeg } from './downscaleImageBlob';

const heicExtensions = ['.heic', '.heif'];
const maxConvertedFileSize = 5 * 1024 * 1024;
const jpegQuality = 0.8;
const downscaledMaxEdge = 4096;

export const isHeicFile = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    return heicExtensions.some((extension) => fileName.endsWith(extension));
};

const replaceExtensionWithJpg = (fileName: string): string => {
    const dotIndex = fileName.lastIndexOf('.');
    const baseName = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
    return `${baseName}.jpg`;
};

// Converts a HEIC/HEIF file to JPEG in the browser (HEIC never reaches the
// backend, which cannot decode it). Non-HEIC files pass through untouched.
// If the conversion exceeds the 5 Mb upload limit, the result is downscaled
// via canvas — never by re-running the slow HEIC decode.
export const convertHeicToJpeg = async (file: File): Promise<File> => {
    if (!isHeicFile(file)) {
        return file;
    }

    const heic2any = (await import('heic2any')).default;
    const result = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: jpegQuality,
    });

    let convertedBlob = Array.isArray(result) ? result[0] : result;
    if (convertedBlob.size > maxConvertedFileSize) {
        convertedBlob = await downscaleImageBlobToJpeg(
            convertedBlob,
            downscaledMaxEdge,
            jpegQuality
        );
    }

    return new File([convertedBlob], replaceExtensionWithJpg(file.name), {
        type: 'image/jpeg',
        lastModified: file.lastModified,
    });
};
